import React, {
  createContext, useContext, useState, useMemo, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useAuth } from 'context/AuthContext';
import { useGetProfileQuery } from 'app/api/rcpApiSlice';
import LoaderSpinner from 'components/atoms/Loader/LoaderSpinner';
import { detectLocale, createTranslator } from './index';

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const { user, isLoading: authLoading } = useAuth();
  const token = user?.id_token;
  const {
    data: profileData,
    isLoading: profileLoading,
    isFetching: profileFetching,
  } = useGetProfileQuery(token, { skip: !token });
  const profileLang = profileData?.user?.cultureCode;

  const [locale, setLocale] = useState(() => detectLocale());

  // Update locale state when profileLang or token changes
  useEffect(() => {
    setLocale(detectLocale(profileLang));
  }, [profileLang, token]);

  // Derived values for the current render to ensure NO flash.
  const currentLocale = useMemo(() => (profileLang ? detectLocale(profileLang) : locale), [profileLang, locale]);
  const currentT = useMemo(() => createTranslator(currentLocale), [currentLocale]);
  const currentValue = useMemo(() => ({ locale: currentLocale, setLocale, t: currentT }), [currentLocale, currentT]);

  const params = new URLSearchParams(window.location.search);
  const hasUrlOverride = params.has('lng');

  const isWaitingForProfile = token && (profileLoading || (profileFetching && !profileData)) && !hasUrlOverride;

  if (authLoading || isWaitingForProfile) {
    return <LoaderSpinner />;
  }

  return (
    <LocaleContext.Provider value={currentValue}>
      {children}
    </LocaleContext.Provider>
  );
}

LocaleProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access translations in any component.
 *
 * Usage:
 *   const { t, locale, setLocale } = useTranslation();
 *   t('COMPONENT.LOG_IN.MENU.HEADING')  // → "Welcome to your Roland Account."
 */
export function useTranslation() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within a LocaleProvider');
  }
  return ctx;
}
